<template>
    <div class="wiagro_risk_analysis">
        <h5 v-show="!showGraph"
            class="text-center text-invalid">
            {{$t('RISK_ANALYSIS.NO_SECTIONS') }}
        </h5>
        <div v-show="showGraph">
            <el-row v-loading="loading" class="ml-n4  ml-md-0 width-sm-110" style="height: 200px" id="chart">

            </el-row>
            <div class="d-flex justify-content-center align-items-center mb-4">
                <el-slider
                        class="w-75"
                        :max="52"
                        :min="0"
                        v-model="week"
                        show-input-controls
                        :marks="marks"
                        :format-tooltip="formatDate"
                        @change="onWeekUpdate"/>
            </div>
        </div>
    </div>
</template>

<script>
    import * as am4core from "@amcharts/amcharts4/core";
    import * as am4charts from "@amcharts/amcharts4/charts";
    import Rainbow from 'rainbowvis.js';
    import api from "../../../utils/api/api";
    import lang from "../../../lang/es.js"

    let rainbow = new Rainbow();
    rainbow.setNumberRange(1, 100);
    rainbow.setSpectrum("#37B34A", "#ffb400", "#c4183c");

    am4core.options.commercialLicense = true;
    export default {
        name: "riskAnalysisChart",
        props: {
            silo: {
                type: Object
            },
            site: {
                type: Object
            },
            sections: {
                type: Array
            }
        },
        data() {
            return {
                week: 0,
                chart: undefined,
                loading: false,
                marks: {
                    0: this.$t("el.datepicker.today"),
                    10: this.formatDate(10),
                    20: this.formatDate(20),
                    30: this.formatDate(30),
                    40: this.formatDate(40),
                    52: this.formatDate(52)
                }
            }
        },
        mounted() {
            this.updateRisk(this.site, this.silo, this.sections, 0).then(value => {
                this.drawChart(value);
            });
        },
        watch: {
            sections: function (newSections) {
                this.updateRisk(this.site, this.silo, newSections, 0).then(value => {
                    if (this.chart === undefined) {
                        this.drawChart(value);
                    }
                });

                this.week = 0;
            }
        },
        computed: {
            showGraph() {
                return !(this.silo === undefined || this.sections === undefined || this.sections.length === 0)
            }
        },
        methods: {
            formatDate(week) {
                let now = new Date(new Date().getTime() + week * 1000 * 60 * 60 * 24 * 7);
                const langKeys = Object.keys(lang.el.datepicker.months);
                return now.getDate() + "-" + (this.$t('el.datepicker.months.' + langKeys[now.getMonth()]));// + "-" + now.getFullYear();
            },
            onWeekUpdate(newWeek) {
                this.updateRisk(this.site, this.silo, this.sections, newWeek);
            },
            updateRisk(site, silo, sections, newWeek) {
                if (!(site !== undefined && silo !== undefined && sections !== undefined)) {
                    return Promise.resolve([]);
                }
                this.loading = true;
                let riskPromises = [];
                sections.forEach(section => {
                    let riskPromise = api.getSectionRisk(site.id, silo.id, section.seccion, newWeek).then(risk => {
                        risk.sectionName = this.$tc('COMMON.SECTION', 1) + " " + section.seccion;
                        risk.sectionStatus = section.status;
                        return risk;
                    });
                    riskPromises.push(riskPromise.catch(reason => {
                        reason.sectionName = this.$tc('COMMON.SECTION', 1) + " " + section.seccion;
                        reason.sectionStatus = section.status;
                        return reason;
                    }));
                });
                return Promise.all(riskPromises).then(sectionsRisks => {
                    let risks = [];
                    sectionsRisks.forEach(sectionRisk => {
                        let risk = 0;
                        let label = this.$t('COMMON.NO_DATA');
                        let color = undefined;
                        if (sectionRisk.risk !== undefined) {
                            risk = sectionRisk.risk;
                            color = am4core.color('#' + rainbow.colorAt(sectionRisk.risk));
                            label = undefined;
                        }
                        risks.push({
                            name: sectionRisk.sectionName,
                            risk: risk,
                            color: color,
                            label: label
                        })
                    });
                    if (this.chart !== undefined) {
                        this.chart.data = risks;
                    }
                    this.loading = false;
                    return risks;
                }).catch(reason => {
                    this.loading = false;
                    console.error(reason);
                    return [];
                });
            },
            drawChart: function (data) {
                let chart = am4core.create("chart", am4charts.XYChart);
                this.chart = chart;

                chart.data = data.map(items => {
                    items.color = am4core.color('#' + rainbow.colorAt(items.risk));
                    return items;
                });

                let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
                categoryAxis.dataFields.category = "name";
                categoryAxis.renderer.grid.template.strokeOpacity = 0;


                let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
                valueAxis.renderer.grid.template.strokeOpacity = 0;
                valueAxis.min = 0;
                valueAxis.max = 100;

                let series = chart.series.push(new am4charts.ColumnSeries);
                series.dataFields.valueY = "risk";
                series.dataFields.categoryX = "name";

                let columnTemplate = series.columns.template;
                columnTemplate.propertyFields.fill = "color";
                columnTemplate.opacity = 0.6;
                columnTemplate.width = am4core.percent(50);
                columnTemplate.maxWidth = 66;
                columnTemplate.strokeOpacity = 0;


                let bullet2 = series.bullets.push(new am4charts.LabelBullet());
                bullet2.label.text = "{risk}";
                bullet2.label.fill = am4core.color("#fff");
                bullet2.zIndex = 1;
                bullet2.fontSize = 11;
                bullet2.dy = 10;
                bullet2.interactionsEnabled = false;


                let bullet = series.bullets.push(new am4charts.LabelBullet());
                bullet.label.text = '{label}';
                bullet.label.fill = am4core.color("#aaa");
                bullet.fontSize = 10;
                bullet.label.truncate = false;
                bullet.label.hideOversized = false;
                bullet.locationY = 1;
                bullet.dy = -10;


            }
        },
        beforeDestroy() {
            if (this.chart) {
                this.chart.dispose();
            }
        }
    }
</script>

<style lang="scss">
    @import '~bootstrap/scss/_functions';
    @import '~bootstrap/scss/_variables';
    @import '~bootstrap/scss/mixins/_breakpoints';

    .wiagro_risk_analysis {

        .width-sm-110 {
            @include media-breakpoint-down(sm) {
                width: 110%;
            }
        }

        .el-slider__marks-text {

            width: 48px
        }
    }

</style>